import {KEY_POINT_TOKENS} from '../constants/tokens';
import {checkIfRuinsIncomeGlowToken} from '../configs/ruinsIncomeGlow';
import {findOwner} from '../utils/gameState/findOwner';
import {checkIfHexBlockedByEnemyUnit} from '../utils/gameState/checkIfHexBlockedByEnemyUnit';
import {calcRuinsIncome} from '../utils/gameState/income/calcRuinsIncome';
import {
  getRuinsIncreasedIncomeStartRound,
  selectRuinsIncomeRoundLimit,
} from '../utils/gameState/income/checkIfRoundWithIncreasedRuinsIncome';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectHexes,
  selectPlayers,
  selectSpecialBuildings,
  selectUnits,
} from '../redux/reducers/sharedStateReducer/selectors';

// Ячейка — шестиугольник с плоским верхом: половина ширины и высоты в долях радиуса
const HEX_HALF_WIDTH_SHARE = 1;
const HEX_HALF_HEIGHT_SHARE = 0.87;

/**
 * Чистые расчеты анимации роста дохода руин (см. RuinsIncomeAnimation): что
 * показывать над какими руинами, каким кадром камеры и когда до них дойдет
 * волна. Без Phaser — проверяются тестами напрямую
 */

/**
 * Руины на поле и их настоящий доход до и после начала окна: те же слагаемые,
 * что считает игра (calcRuinsIncome), — руины игрока с Фанатизмом показывают
 * 4 → 6, а не 2 → 4. Заблокированные врагом показаны потухшими, как на плашке
 * дохода поля (renderKeyPointIncome)
 * @param {Object} state
 * @returns {Array<{hexKey: string, tokenType: string, before: number, after: number, isOwned: boolean, isBlocked: boolean}>}
 */
export const buildRuinsIncomeChanges = (state) => {
  const roundLimit = selectRuinsIncomeRoundLimit(state);
  const startRound = getRuinsIncreasedIncomeStartRound(roundLimit);
  if (startRound === null) {
    return [];
  }
  const players = selectPlayers(state) || [];
  const citiesCache = selectCitiesCache(state);
  const units = selectUnits(state);
  const hexes = selectHexes(state) || {};
  const specialBuildings = selectSpecialBuildings(state);
  const buildingsDamage = selectBuildingsDamage(state);

  return Object.entries(hexes)
    .filter(([, tokenType]) => checkIfRuinsIncomeGlowToken(tokenType))
    .map(([hexKey, tokenType]) => {
      const owner = findOwner(hexKey, players, citiesCache);
      return {
        hexKey,
        tokenType,
        before: calcRuinsIncome(tokenType, owner, startRound - 1, roundLimit),
        after: calcRuinsIncome(tokenType, owner, startRound, roundLimit),
        isOwned: Boolean(owner),
        isBlocked: Boolean(owner)
          && checkIfHexBlockedByEnemyUnit(hexKey, tokenType, owner.id, units, hexes, specialBuildings, buildingsDamage),
      };
    });
};

/** Великие руины — тяжелее обычных: вспышка и лучи у них крупнее */
export const checkIfSuperRuins = (tokenType) => tokenType === KEY_POINT_TOKENS.SUPER_RUINS;

/**
 * Кадр камеры: все поле по вертикали в свободной области, а над ним — место
 * для медальонов верхних руин. Руины должны быть в кадре и по горизонтали: на
 * узком экране камера отъезжает дальше, а центр встает на середину руин, если
 * поле целиком не влезает
 * @param {{centerX: number, centerY: number, width: number, height: number}} playArea
 * @param {Array<{centerX: number, centerY: number, size: number}>} ruinHexes
 * @param {{width: number, height: number}} freeAreaPx
 * @param {{fitShare: number, maxZoom: number}} config
 * @param {number} [topReservePx] - сколько оставить над полем
 * @returns {{x: number, y: number, zoom: number}}
 */
export const calcRuinsIncomeCameraFrame = (playArea, ruinHexes, freeAreaPx, {fitShare, maxZoom}, topReservePx = 0) => {
  const usableWidth = freeAreaPx.width * fitShare;
  const usableHeight = Math.max(1, freeAreaPx.height * fitShare - topReservePx);
  const xs = ruinHexes.flatMap((hex) => [hex.centerX - hex.size, hex.centerX + hex.size]);
  const ruinsLeft = Math.min(...xs);
  const ruinsRight = Math.max(...xs);
  const zoom = Math.min(
    maxZoom,
    usableHeight / Math.max(1, playArea.height),
    usableWidth / Math.max(1, ruinsRight - ruinsLeft)
  );
  const isFieldFitsWidth = playArea.width * zoom <= usableWidth;
  return {
    x: isFieldFitsWidth ? playArea.centerX : (ruinsLeft + ruinsRight) / 2,
    y: playArea.centerY - topReservePx / 2 / zoom,
    zoom,
  };
};

/**
 * Сторона медальона в пикселях канвы: доля кадра в пределах границ в CSS-пикселях
 * @param {{width: number, height: number}} areaPx
 * @param {number} canvasPxPerCssPx
 * @param {{heightShare: number, widthShare: number, minCssPx: number, maxCssPx: number}} plate
 */
export const calcRuinsIncomePlateSizePx = (areaPx, canvasPxPerCssPx, {heightShare, widthShare, minCssPx, maxCssPx}) => (
  Math.min(
    maxCssPx * canvasPxPerCssPx,
    Math.max(minCssPx * canvasPxPerCssPx, Math.min(areaPx.height * heightShare, areaPx.width * widthShare))
  )
);

/**
 * Где встанут медальоны: каждый рядом со своими руинами, чтобы было видно, чьи это
 * числа, без указателей. Сверху, а если там другие руины или соседний медальон — с
 * другой стороны. Руины стоят и вплотную: верхние в скоплении занимают места над
 * ним, нижние — под ним. Закрывать руины медальоны не должны. Места нет нигде —
 * медальон остается сверху: лучше наложение, чем число вдали от руин
 * @param {Array<{centerX: number, centerY: number, size: number}>} hexes
 * @param {number} medallionSize - видимая сторона медальона на поле
 * @param {{minX: number, maxX: number, minY: number, maxY: number}} bounds - куда можно ставить центры
 * @param {{gapAboveHexShare: number, overlapTolerance: number}} badge
 * @returns {Array<{x: number, y: number}>} в порядке hexes
 */
export const placeRuinsIncomeMedallions = (hexes, medallionSize, bounds, {gapAboveHexShare, overlapTolerance}) => {
  const half = medallionSize / 2;
  const reach = medallionSize * overlapTolerance;
  const clampToBounds = ({x, y}) => ({
    x: Math.min(bounds.maxX, Math.max(bounds.minX, x)),
    y: Math.min(bounds.maxY, Math.max(bounds.minY, y)),
  });
  const checkIfInBounds = ({x, y}) => x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
  const checkIfCoversRuins = ({x, y}) => hexes.some((hex) => (
    Math.abs(x - hex.centerX) < reach / 2 + hex.size * HEX_HALF_WIDTH_SHARE
    && Math.abs(y - hex.centerY) < reach / 2 + hex.size * HEX_HALF_HEIGHT_SHARE
  ));
  const buildCandidates = (hex) => {
    const offsetY = hex.size * (HEX_HALF_HEIGHT_SHARE + gapAboveHexShare) + half;
    const offsetX = hex.size * (HEX_HALF_WIDTH_SHARE + gapAboveHexShare) + half;
    return [
      [0, -offsetY], [0, offsetY], [-offsetX, 0], [offsetX, 0],
      [-offsetX, -offsetY], [offsetX, -offsetY], [-offsetX, offsetY], [offsetX, offsetY],
    ].map(([dx, dy]) => ({x: hex.centerX + dx, y: hex.centerY + dy}));
  };

  const placed = [];
  const result = new Array(hexes.length);
  // Сверху вниз: верхним руинам и достаются места над скоплением
  hexes
    .map((hex, index) => ({hex, index}))
    .sort((a, b) => a.hex.centerY - b.hex.centerY)
    .forEach(({hex, index}) => {
      const candidates = buildCandidates(hex);
      const free = candidates.find((point) => (
        checkIfInBounds(point)
        && !checkIfCoversRuins(point)
        && !placed.some((other) => Math.abs(point.x - other.x) < reach && Math.abs(point.y - other.y) < reach)
      ));
      const position = free || clampToBounds(candidates[0]);
      placed.push(position);
      result[index] = position;
    });
  return result;
};

/**
 * Когда до каждых руин дойдет волна от центра поля: ближние раньше, самые
 * дальние — на spreadMs
 * @param {Array<{hexKey: string, x: number, y: number}>} points
 * @param {{x: number, y: number}} origin
 * @param {number} spreadMs
 * @returns {{delays: Object<string, number>, maxDistance: number}}
 */
export const calcRuinsIncomeWaveDelays = (points, origin, spreadMs) => {
  const distances = points.map(({x, y}) => Math.hypot(x - origin.x, y - origin.y));
  const maxDistance = Math.max(0, ...distances);
  const delays = {};
  points.forEach(({hexKey}, index) => {
    delays[hexKey] = maxDistance > 0 ? (distances[index] / maxDistance) * spreadMs : 0;
  });
  return {delays, maxDistance};
};
